import { useCallback, useEffect, useMemo } from "react";

import { Values } from "src/modules/SubmissionPeriods/validation";
import { useCreateSubmissionPeriodMutation } from "src/services/state-submission";
import { isApiError } from "src/utils/apiError";
import { toastError, toastSuccess } from "src/utils/toast";

export const useInitialValues = (): Values => {
  return useMemo(() => {
    const values: Values = {
      name: "",
      submissionType: "inventory",
      dueDate: "",
      lockedDate: "",
      overdueSetting: "",
      active: true,
    };

    return values;
  }, []);
};

export const useCreateSubmissionPeriod: (onClose: () => void) => {
  errors: string[] | undefined;
  onSubmit: (values: Values) => Promise<void>;
} = (onClose) => {
  const [
    trigger,
    { data, error, isError, isLoading, isSuccess, isUninitialized },
  ] = useCreateSubmissionPeriodMutation();
  useEffect(() => {
    if (!isUninitialized && !isLoading && isSuccess) {
      toastSuccess("Submission Period has been created");
    }
  }, [data, isError, isLoading, isSuccess, isUninitialized]);

  const onSubmit = useCallback(
    async (values: Values) => {
      const {
        name,
        submissionType,
        dueDate,
        lockedDate,
        overdueSetting,
        active,
      } = values;

      const endOfDayDueDate = new Date(dueDate)
      endOfDayDueDate.setUTCHours(23, 59, 59, 0);

      const endOfDateLockedDate = lockedDate ? new Date(lockedDate) : undefined;
      endOfDateLockedDate?.setUTCHours(23, 59, 59, 0);

      const submissionPeriod = {
        lockedDate: endOfDateLockedDate ? endOfDateLockedDate.toISOString() : null,
        dueDate: endOfDayDueDate.toISOString(),
        active,
        name,
        submissionType,
        overdueSetting,
      };

      try {
        await trigger({ submissionPeriod }).unwrap();
        onClose();
      } catch (error) {
        if (error !== null && typeof error === "object" && "data" in error) {
          const data = error.data;
          if (
            data !== null &&
            typeof data === "object" &&
            "consumerErrorMessage" in data
          ) {
            const errorMessage = data.consumerErrorMessage as string;
            if (errorMessage.includes("BadRequestException")) {
              toastError(errorMessage.replace("BadRequestException: ", ""));
              return;
            }
          }
        }
        toastError("An error occurred. Submission Period could not be created.");
      }
    },
    [trigger]
  );

  const errors = useMemo(() => {
    if (!error || !isError) {
      return undefined;
    }

    if (isApiError(error)) {
      return [error.data?.error];
    }

    return ["Please try again or contact support@120water.com"];
  }, [error, isError]);

  return useMemo(() => ({ errors, onSubmit }), [errors, onSubmit]);
};
